<template>
  <div class="app-body_show container-fluid pt-4 pb-5">
    <div class="row">
      <div class="col-md-3">
        <scientific-awards
          v-if="isLoaded && !isEmpty(researchGroup.scientific_awards)"
          :researchers="researchGroup.scientific_awards"
          class="card mb-4"
        />
        <!--
          Show component while data is loading,
          if data is loaded and no grph data present, hide it
        -->
        <flex-chart
          v-if="!isGraphLoaded || !(isGraphLoaded && !graphData.publications)"
          :isLoaded="!!(isGraphLoaded && graphData.publications)"
          :graphData="parsedGraphData.publications || []"
          :legend="parsedGraphData.publicationsLegend || {}"
          :name="'Publications'"
          :scope="'research_groups'"
          :chartType="'bar'"
          :color="'#43AEC5'"
          isSmall
          withContainer>
        </flex-chart>

        <flex-chart
          v-if="!isGraphLoaded || !(isGraphLoaded && !graphData.citations)"
          :isLoaded="!!(isGraphLoaded && graphData.citations)"
          :graphData="parsedGraphData.citations || []"
          :legend="parsedGraphData.citationsLegend || {}"
          :name="'Citations'"
          :scope="'research_groups'"
          :chartType="'line'"
          :color="'#EFA9BB'"
          isSmall
          withContainer>
        </flex-chart>

        <!-- render research group type -->
        <research-group-type v-if="!isLoaded || canEdit || !isEmpty(researchGroup.research_group_types)"
          :isLoaded="isLoaded"
          :researchGroup="researchGroup"
          class="card mb-4">
        </research-group-type>

        <!-- render research interests -->
        <research-interests v-if="!isLoaded || canEdit || !isEmpty(researchGroup.research_interests)"
          :isLoaded="isLoaded"
          :researchGroup="researchGroup"
          class="card mb-4">
        </research-interests>

        <!-- render discipline -->
        <disciplines v-if="!isLoaded || canEdit || !isEmpty(researchGroup.disciplines)"
          :isLoaded="isLoaded"
          :researchGroup="researchGroup"
          class="card mb-4">
        </disciplines>

        <!-- render research type -->
        <research-type v-if="!isLoaded || canEdit || !isEmpty(researchGroup.research_types)"
          :isLoaded="isLoaded"
          :researchGroup="researchGroup"
          :canEdit="canEdit"
          class="card mb-4">
        </research-type>

        <!-- render modal organism -->
        <model-organism v-if="!isLoaded || canEdit || !isEmpty(researchGroup.model_organisms)"
          :isLoaded="isLoaded"
          :canEdit="canEdit"
          :researchGroup="researchGroup"
          class="card mb-4">
        </model-organism>

        <!-- render disease -->
        <disease v-if="!isLoaded || canEdit || !isEmpty(researchGroup.diseases)"
          :isLoaded="isLoaded"
          :canEdit="canEdit"
          :researchGroup="researchGroup"
          class="card mb-4">
        </disease>

        <!-- render sector -->
        <sector v-if="!isLoaded || (researchGroup.category === 'professional' && (canEdit || !isEmpty(researchGroup.sectors)))"
          :isLoaded="isLoaded"
          :researchGroup="researchGroup"
          class="card mb-4">
        </sector>

        <!-- render therapeutic -->
        <therapeutic v-if="!isLoaded || canEdit || !isEmpty(researchGroup.therapeutics)"
          :isLoaded="isLoaded"
          :researchGroup="researchGroup"
          class="card mb-4">
        </therapeutic>

        <!-- render medical technology -->
        <medical-technology v-if="!isLoaded || canEdit || !isEmpty(researchGroup.medical_technologies)"
          :isLoaded="isLoaded"
          :researchGroup="researchGroup"
          class="card mb-4">
        </medical-technology>
      </div>

      <div class="col-md-6">
        <bar-search-categories
          :indexes="indexes"
          :routeName="'research_group.explore'"
          :isSticky="false"
          :indexCount="6"
          :showEmptyBar="false"
        />
        <search-bar
          v-if="isLoaded && hasResearch"
          class="mb-4"
          :state="searchFilterObject"
          :indexes="indexes"
        />

        <!-- render research group mission -->
        <research-group-mission v-if="!isEmpty(researchGroup.description) || canView"
          :researchGroup="researchGroup"
          :isLoaded="isLoaded"
          class="card mb-4">
        </research-group-mission>

        <webpages-container-preview v-if="isLoaded"
          :searchFilterObject="searchFilterObject"
          :index="'web_pages_research_groups'"
          :route="{ name: 'research_group', slug: researchGroup.slug_values[0] }"
          :totalDocuments="3"
          class="mt-4"
        />

        <tree-chart
          :isLoaded="isTreeGraphLoaded"
          :graphData="treeGraphData"
          :parent="{
            label: researchGroup.name,
            value: researchGroup.slug_text
          }"
          :type="'organisation'"
          :exploreRoute="exploreRoute"
        />

        <!-- render research group research -->
        <research-group-research v-if="canEdit"
          :researchGroup="researchGroup"
          :isLoaded="isLoaded"
          class="card mb-4">
        </research-group-research>

        <!-- render research group about -->
        <research-group-about v-if="!isEmpty(researchGroup.about) || canView"
          :researchGroup="researchGroup"
          :isLoaded="isLoaded"
          class="card mb-4">
        </research-group-about>

        <search-container-preview v-if="isLoaded && !canEdit"
          :searchFilterObject="searchFilterObject"
          :indexes="['research_topics']"
          :route="{ name: 'research_group', slug: researchGroup.slug_values[0] }"
          :class="'mt-4'"
          :scope="'research_group'">
        </search-container-preview>

        <search-container-preview v-if="isLoaded"
          :searchFilterObject="searchFilterObject"
          :indexes="['projects', 'publications', 'clinical_trials', 'patents']"
          :suggestionIndexes="['projects', 'publications', 'clinical_trials', 'patents']"
          :route="{ name: 'research_group', slug: researchGroup.slug_values[0] }"
          :class="'mt-4'">
        </search-container-preview>
      </div>

      <div class="col-md-3">
        <profile-actions
          scope="research_groups"
          :item="researchGroup"
          :contacts="contacts"
          :isLoaded="isLoaded"
        />

        <!-- research group contact information -->
        <contact v-if="isLoaded"
          :isLoaded="isLoaded"
          :item="researchGroup"
          :scope="`research_groups`"
          :canEdit="canView"
        />

        <active-email-address
          v-if="isLoaded && $currentUser"
          :title="`Email addresses of contacts`"
          :emailsObject="researchGroup.primary_emails"
        />

        <div v-if="isLoaded" class="card mb-4">
          <div class="card-body py-2 invite-box">
            <!-- invite community -->
            <invite-button-community v-if="canInvite" :researchGroup="researchGroup"/>

            <hr v-if="canInvite && !canView && $currentUser && researchGroup.user_invite_action !== 'declined' && researchGroup.user_invite_action !== 'confirmed'" class="hr-outlined my-2" />
            <!-- ask to join -->
            <a v-if="!canView && !researchGroup.user_invite_action" href="#" class="d-block block-fa clearfix width--100per" @click.prevent="openModal()">
              <i class="fa fa-user t-sm pr-3 t-secondary"></i><span>Belong to this research group? Ask to join on AcademicLabs.</span>
            </a>

            <!-- pending request -->
            <router-link v-if="!canView && researchGroup.user_invite_action === 'invite_request'" :to="{ name: 'researcher.invite', params: { id: $currentUser.slug_values[0]} }" class="d-block block-fa clearfix width--100per t-green">
              <i class="fa fa-user t-sm pr-3 t-secondary"></i><span>Waiting for approval to join</span>
            </router-link>

            <!-- pending request -->
            <router-link v-if="!canView && researchGroup.user_invite_action === 'invite'" :to="{ name: 'researcher.invite', params: { id: $currentUser.slug_values[0]} }" class="d-block block-fa clearfix width--100per t-green">
              <i class="fa fa-user t-sm pr-3 t-secondary"></i><span>Invitation pending to join this research group</span>
            </router-link>
          </div>
        </div>

        <div v-if="isLoaded && !isEmpty(researchGroup.communities)"  class="card mb-4">
          <div class="card-body py-3">
            <h3 class="mb-3 t-500">
              Participating in {{ researchGroup.communities.length }} {{ 'community' | pluralize(researchGroup.communities.length) }}
              <i class="fa fa-globe d-block pull-right t-placeholder"></i>
            </h3>

            <ul class="list-unstyled mb-0 mt-3 clearfix">
              <li v-for="(community, index) in researchGroup.communities" :key="index">
                <router-link :to="{ name: 'community.show', params: { id: community.slug_values[0] }}">
                  {{ community.name }}
                </router-link>
              </li>
            </ul>
          </div>
        </div>

        <identifiers :item="researchGroup" />
      </div>
    </div>
  </div>
</template>

<script>
import ResearchGroupMission from '@/components/research_group/show/Mission'
import ResearchGroupAbout from '@/components/research_group/show/About'
import ResearchGroupResearch from '@/components/research_group/show/Research'
import ResearchGroupType from '@/components/research_group/show/ResearchGroupType'
import Contact from '@/components/contact/Show'
import InviteButtonCommunity from '@/components/invite/button/ResearchGroupToCommunity'
import Sector from '@/components/research_group/show/Sector'
import Therapeutic from '@/components/research_group/show/Therapeutic'
import MedicalTechnology from '@/components/research_group/show/MedicalTechnology'
import Disease from '@/components/research_group/show/Disease'
import ModelOrganism from '@/components/research_group/show/ModelOrganism'
import ResearchType from '@/components/research_group/show/ResearchType'
import ResearchInterests from '@/components/research_group/show/ResearchInterests'
import Disciplines from '@/components/research_group/show/Disciplines'
import SearchContainerPreview from '@/containers/SearchPreview'
import FlexChart from '@/components/shared/chart/FlexChart'
import SearchBar from '@/components/search/BarDashboard'
import TreeChart from '@/components/shared/chart/Tree'
import withTree from '@/components/shared/chart/withTree'
import BarSearchCategories from '@/components/search/BarSearchCategories'
import Identifiers from '@/components/qa/Identifiers'
import ProfileActions from '@/components/shared/profile/Actions'
import ScientificAwards from '@/components/research_group/show/ScientificAwards'
import WebpagesContainerPreview from '@/containers/WebpagesPreview'
import ActiveEmailAddress from '@/components/shared/ActiveEmailAddress'

import SearchService from '@/services/search'
import ResearchGroupService from '@/services/research_group'

import searchHelpers from '@/mixins/search/helpers'

export default {
  name: 'researchGroupShow',
  mixins: [searchHelpers, withTree],
  components: {
    ResearchGroupResearch,
    ResearchGroupMission,
    ResearchGroupAbout,
    ResearchGroupType,
    Contact,
    InviteButtonCommunity,
    Sector,
    Therapeutic,
    MedicalTechnology,
    Disease,
    ModelOrganism,
    ResearchType,
    ResearchInterests,
    Disciplines,
    SearchContainerPreview,
    FlexChart,
    SearchBar,
    ScientificAwards,
    TreeChart,
    BarSearchCategories,
    Identifiers,
    ProfileActions,
    WebpagesContainerPreview,
    ActiveEmailAddress
  },
  props: {
    isLoaded: {
      type: Boolean,
      required: true
    },
    researchGroup: {
      type: Object,
      required: true
    },
    searchFilterObject: {
      type: Object,
      required: true
    },
    hasResearch: {
      type: Boolean,
      required: true
    },
    indexes: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      isGraphLoaded: false,
      graphData: {},
      contacts: []
    }
  },
  computed: {
    canInvite () {
      return !!(this.$currentUser && this.$ls.getUser() && this.$currentUser.can_edit_community)
    },
    canEdit () {
      return this.$can.edit('research_group', this.researchGroup.rw_access)
    },
    canView () {
      return this.$can.view('research_group', this.researchGroup.rw_access)
    },
    parsedGraphData () {
      if (!this.graphData || this.isEmpty(this.graphData)) {
        return {}
      }
      const pubs = this.formatGraphData(this.graphData.publications)
      const cits = this.formatGraphData(this.graphData.citations)
      const firstPub = pubs.findIndex(p => p.y > 0)
      const firstCit = cits.findIndex(p => p.y > 0)
      const earliestIndex = firstPub < firstCit ? firstPub : firstCit

      return {
        publicationsLegend: this.getTotals(this.graphData.publications),
        citationsLegend: this.getTotals(this.graphData.citations),
        publications: pubs.slice(Math.max(earliestIndex, 0)),
        citations: cits.slice(Math.max(earliestIndex, 0))
      }
    }
  },
  mounted () {
    if (this.isLoaded) {
      this.fetchGraphData()
      this.fetchTreeGraphData(
        this.getSearchObjectGraphData(this.searchFilterObject, ['publications']),
        this.researchGroup.organisations ? this.researchGroup.organisations.map(org => org.slug_text) : []
      )
      this.fetchContactInfo()
    }
  },
  watch: {
    isLoaded (newVal, oldVal) {
      if (newVal !== oldVal && newVal) {
        this.fetchGraphData()
        this.fetchTreeGraphData(
          this.getSearchObjectGraphData(this.searchFilterObject, ['publications']),
          this.researchGroup.organisations ? this.researchGroup.organisations.map(org => org.slug_text) : []
        )
        this.fetchContactInfo()
      }
    }
  },
  methods: {
    getTotals (data) {
      let total = 0
      let suggestions = 0
      data.forEach(item => {
        total += item.total
        suggestions += item.total_suggestions
      })
      return {
        total,
        suggestions
      }
    },
    formatGraphData (data) {
      if (!data) {
        return []
      }
      const items = data
        .sort((a, b) => b.year - a.year)
        .reduce((a, b, i, arr) => {
          // If there is no year, or it's super long ago just go to the next value
          if (!b.year || b.year < 1970) return a

          const prevYear = i && arr[i - 1].year
          // Check if there are any gaps between years, add missing dataPoints if needed
          if (prevYear && prevYear < b.year - 1) {
            const count = b.year - prevYear - 1
            for (let j = 0; j < count; j++) {
              a.push({
                x: prevYear + j + 1,
                y: 0
              })
            }
          }
          // Include suggestion counts into the graph data
          a.push({
            x: b.year,
            y: b.count
          })
          return a
        }, [])

      return items.map(i => {
        return {
          ...i,
          year: i.x.toString()
        }
      })
    },
    openModal () {
      !this.$currentUser
        ? this.$events.$emit('modal:login:open')
        : this.$events.$emit('modal:invite_request:open', this.researchGroup.slug_values[0], 'research_group', this.researchGroup)
    },
    fetchGraphData () {
      this.graphData = {}
      this.isGraphLoaded = false
      const searchObject = this.getSearchObjectGraphData(this.searchFilterObject, ['publications'])

      SearchService.graphData(searchObject).then(graphData => {
        this.graphData = graphData
        this.isGraphLoaded = true
      }).catch(err => {
        this.error = this.handleError(err)
        this.isGraphLoaded = true
      })
    },
    exploreRoute (bucket) {
      return {
        name: `research_group.explore`,
        params: {
          category: this.searchFilterObject.index,
          buckets: [`organisations:${bucket}`]
        }
      }
    },
    fetchContactInfo () {
      this.contacts = []

      ResearchGroupService.getContact(this.researchGroup.slug_values[0]).then(contacts => {
        this.contacts = contacts
      }).catch(() => {})
    }
  }
}
</script>
